import { useEffect, useMemo, useState } from 'react';

import useBooleanSetting from './useBooleanSetting';
import useChordGridPlayer from './useChordGridPlayer';
import usePreviewMix, { DEFAULT_CLICK_VOLUME, DEFAULT_MELODY_VOLUME, usePlayLabel } from './usePreviewMix';
import useSampleLoader from './useSampleLoader';
import useSetting from './useSetting';
import { buildChartRun, hasMelodyNotes } from '../utils/chartRun';
import { getBlockGroove, getDrumGroove } from '../utils/drumGrooves';
import {
  getBlockStyle,
  getPadSoundId,
  getRhythmPattern,
  hasBandLayer,
  hasBassLine,
  playsBassAt,
} from '../utils/chordRhythmPatterns';
import { BAND_LAYERS, MUTED_GUITAR_ID } from '../utils/chordBand';
import { BASS_INSTRUMENT_ID } from '../utils/chordBarsMidi';
import { getLayerGain, getPadGain, getPassIntensity, LAYER_ON } from '../utils/chartIntensity';
import { getInstrument, isSampledInstrument } from '../utils/midiInstruments';
import { settingKeys } from '../utils/settings';
import { getBeatsPerBar } from '../utils/songTiming';
import { getPartCallout } from '../utils/partCallouts';
import { canSpeak, getSpeechLang } from '../utils/speech';
import { setSend } from '../utils/effectSends';

// The levels the preview opens at the first time; where they land is kept. The metronome the rest
// of the app plays reads metronome-volume, untouched by these sliders.
export const DEFAULT_INSTRUMENT_VOLUME = 0.75;
// Under the chords rather than level with them: the drums are there to put the style under a chart
// that is still the thing being listened to
export const DEFAULT_DRUM_VOLUME = 0.6;
// With the chords rather than under them: the bass is half the harmony's low end
export const DEFAULT_BASS_VOLUME = 0.7;
// Under the band: a pad is felt more than heard
export const DEFAULT_PAD_VOLUME = 0.7;
// The band's extra players are levelled in their presets, so their sliders start where the pad's does
export const DEFAULT_BAND_VOLUME = 0.7;
// Above the drums: a spoken name has to carry over the band to be any use as a prompt
export const DEFAULT_CALLOUT_VOLUME = 0.7;
// The chords step back under a melody so the tune sits on top. Applied over the slider rather than
// moving it, so turning the melody off brings the chords back to where they were set.
const CHORDS_UNDER_MELODY = 0.6;

// One of the band's extra players: on, and how loud, kept between sessions like the pad
const useBandLayerSetting = (onKey, volumeKey) => {
  const [on, toggle, setOn] = useBooleanSetting(onKey, true);
  const [volume, setVolume] = useSetting(volumeKey, DEFAULT_BAND_VOLUME);

  return { on, toggle, setOn, volume, setVolume };
};

const NO_EFFECTS = {};
const IDLE_LOADER = { samples: { status: 'idle', downloadedIds: new Set() }, instrumentId: '' };
const NO_SEGMENT = -1;

/**
 * Everything needed to hear a chord-bar grid: the clip, the player, and the settings the two
 * controls in ChordBarsTransport move.
 *
 * Lives here rather than in the builder because a chart is heard in two places now — written in
 * the song form, and played back from View — and those two have to agree on what the preview is:
 * the same click balance, the same count-in, the same "Downloading sound…" wait. The builder owns
 * the grid and the instrument; this owns what happens when Play is pressed.
 *
 * @param {string[][]} lines - The grid, as getPartGrid or the builder hands it over
 * @param {object} options
 * @param {string|number} options.bpm - The song's tempo; without one there is no clip to play
 * @param {string} options.timeSignature - Beats per bar
 * @param {string} options.category - The song's category name, 'Praise' or 'Worship', which picks the
 *   groove and the band under block chords
 * @param {string} options.instrumentId - Preset to play the chords with
 * @param {boolean} options.isActive - Whether the modal holding it is open. Doubles as the cue to
 *   fetch Tone, and stops playback when it goes false.
 * @param {number} options.startBar - Bar of the grid Play begins at; 0 is the top of the chart
 * @param {string} options.rhythm - The id of the pattern to play each chord with, from
 *   chordRhythmPatterns.js, or empty for the block chords the grid states outright.
 *   A caller's setting rather than one of this hook's own, unlike the click and the loop: the
 *   builder auditions single chords with it too, and the two have to move together.
 * @param {object} options.melody - The part's MIDI to play along with the chart, as
 *   `{ midiJson, instrumentId, semitones, atBar }`, or null. Its empty lead-in bars are dropped and
 *   it is re-timed to the chart's tempo, so its first bar with notes is the chart's first bar —
 *   or `atBar`, where the clip was played for one instrument of a chart shared between several.
 * @param {number} options.repeatCount - How many times the chart plays when `repeats` is on
 * @param {boolean} options.countInEntry - The chart's first bar sounds over the count-in
 * @param {boolean} options.repeats - Play each part its repeatCount times rather than once
 * @param {object[]} options.entries - Several parts to play one after another in place of `lines`,
 *   each `{ lines, bpm, instrumentId, melody, repeatCount, countInEntry }` — see buildChartRun. The options above
 *   that describe one part are then ignored.
 * @param {object} options.effects - The song's chartEffects, the reverb and delay each player sends
 * @param {function} options.onEffectsChange - Called with the next chartEffects. Without it the
 *   mixer offers no effects to change, though the song's are still heard.
 * @param {string} options.melodySetting - Where the melody's on/off is remembered, for a player
 *   that keeps its own
 */
const useChordBarsPreview = (
  lines,
  {
    bpm,
    timeSignature = '4/4',
    category = '',
    instrumentId,
    isActive = false,
    startBar = 0,
    rhythm = '',
    melody = null,
    repeatCount = 1,
    countInEntry = false,
    repeats = false,
    entries = null,
    effects = null,
    onEffectsChange = null,
    melodySetting = settingKeys.chordBarsMelody,
  },
) => {
  const mix = usePreviewMix();
  const [instrumentVolume, setInstrumentVolume] = useSetting(
    settingKeys.chordBarsInstrumentVolume,
    DEFAULT_INSTRUMENT_VOLUME,
  );
  // Kept between sessions, unlike the click and the loop: whether a style is heard with its band
  // under it is a taste, and it holds from one chart to the next
  const [drums, toggleDrums, setDrums] = useBooleanSetting(settingKeys.chordBarsDrums, true);
  // Kept for the same reason, and off by default: it changes how every chart sounds, so it is a
  // thing you go and turn on rather than something the app decides for a song you already know
  const [voicing, toggleVoicing] = useBooleanSetting(settingKeys.chordBarsVoicing, false);
  const [drumVolume, setDrumVolume] = useSetting(settingKeys.chordBarsDrumVolume, DEFAULT_DRUM_VOLUME);
  const [loop, setLoop] = useState(false);
  const [melodyOn, toggleMelody] = useBooleanSetting(melodySetting, false);
  const [melodyVolume, setMelodyVolume] = useSetting(settingKeys.melodyVolume, DEFAULT_MELODY_VOLUME);
  // On by default: a style only has a bass player because it was written to be heard with one
  const [bassOn, toggleBass, setBassOn] = useBooleanSetting(settingKeys.chordBarsBass, true);
  const [bassVolume, setBassVolume] = useSetting(settingKeys.chordBarsBassVolume, DEFAULT_BASS_VOLUME);
  const [padOn, togglePad, setPadOn] = useBooleanSetting(settingKeys.chordBarsPad, true);
  const [padVolume, setPadVolume] = useSetting(settingKeys.chordBarsPadVolume, DEFAULT_PAD_VOLUME);
  const bandSettings = {
    brass: useBandLayerSetting(settingKeys.chordBarsBrass, settingKeys.chordBarsBrassVolume),
    guitar: useBandLayerSetting(settingKeys.chordBarsGuitar, settingKeys.chordBarsGuitarVolume),
    acoustic: useBandLayerSetting(settingKeys.chordBarsAcoustic, settingKeys.chordBarsAcousticVolume),
    violin: useBandLayerSetting(settingKeys.chordBarsViolin, settingKeys.chordBarsViolinVolume),
  };

  const melodySource = melody?.midiJson;
  const melodySemitones = melody?.semitones || 0;
  const melodyInstrumentId = melody?.instrumentId || '';
  const melodyAtBar = melody?.atBar || 0;

  // Memoised on its inputs rather than the objects the caller passes, which are new every render
  const single = useMemo(
    () => ({
      lines,
      bpm,
      instrumentId,
      repeatCount,
      countInEntry,
      melody: melodySource
        ? { midiJson: melodySource, semitones: melodySemitones, instrumentId: melodyInstrumentId, atBar: melodyAtBar }
        : null,
    }),
    [lines, bpm, instrumentId, repeatCount, countInEntry, melodySource, melodySemitones, melodyInstrumentId, melodyAtBar],
  );
  const parts = useMemo(() => entries || [single], [entries, single]);
  const hasMelody = parts.some((part) => hasMelodyNotes(part.melody?.midiJson));
  // The band under block chords, for parts that play them
  const blockStyle = getBlockStyle(getBeatsPerBar(timeSignature), category);
  // Whether any part plays a style with this layer at a level that brings it in, on any of its passes.
  // The parts played through can each have their own style.
  const hasLayer = (styleHas, layer, levelHas = (part, level) => getLayerGain(part.layers, layer, level) > 0) =>
    parts.some((part) => {
      const id = part.rhythm ?? rhythm;
      const style = id ? getRhythmPattern(id) : blockStyle;
      const passes = Math.max(1, Math.round(part.repeatCount) || 1);

      return (
        !part.drumsOnly &&
        Array.from({ length: passes }, (_, i) => getPassIntensity(part, i + 1)).some(
          (level) => levelHas(part, level) && Boolean(style && styleHas(style, level, part)),
        )
      );
    });
  // Switched on, the bass plays the nearest level's line where its own has nothing
  const hasBass = hasLayer(
    (style, level, part) => (part.layers?.bass === LAYER_ON ? hasBassLine(style) : playsBassAt(style, level)),
    'bass',
  );
  const bassPlays = hasBass && bassOn;
  // Kept on under the quieter parts too
  const hasPad = hasLayer(getPadSoundId, 'pad', (part, level) => getPadGain(part, level) > 0);
  const padPlays = hasPad && padOn;
  // The band's extra players, each shown and played on its own
  const band = BAND_LAYERS.map((layer) => ({
    ...layer,
    ...bandSettings[layer.id],
    has: hasLayer((pattern) => hasBandLayer(pattern, layer.id), layer.id),
  }));
  // A string, so the run is rebuilt only when what plays changes
  const bandPlays = band
    .filter((layer) => layer.has && layer.on)
    .map((layer) => layer.id)
    .join(',');

  const run = useMemo(
    () =>
      buildChartRun(parts, {
        timeSignature,
        rhythm,
        voicing,
        repeats,
        melody: melodyOn,
        bass: bassPlays,
        pad: padPlays,
        band: bandPlays ? bandPlays.split(',') : [],
        category,
      }),
    [parts, timeSignature, rhythm, voicing, repeats, melodyOn, bassPlays, padPlays, bandPlays, category],
  );
  // The pad's recordings to wait on: the first part's, like the chords'
  const padSampledId =
    run?.padNotes.find((note) => isSampledInstrument(getInstrument(note.instrumentId)))?.instrumentId || '';

  // Said once per part, on its first pass, with how many passes follow. Only a run's parts have names,
  // so the switch and the level are remembered here but only offered, and only heard, playing through.
  const [calloutsOn, toggleCallouts] = useBooleanSetting(settingKeys.chordBarsCallouts, false);
  const [calloutVolume, setCalloutVolume] = useSetting(settingKeys.chordBarsCalloutVolume, DEFAULT_CALLOUT_VOLUME);
  const calloutLang = useMemo(getSpeechLang, []);
  const calloutTexts = useMemo(
    () =>
      entries && run
        ? run.segments.map((segment) =>
            segment.pass === 1 ? getPartCallout(entries[segment.entryIndex], segment.passes, calloutLang) : null,
          )
        : null,
    [entries, run, calloutLang],
  );

  // A loop goes round one chart. A run of several — parts, or one part's repeats — has an end, and
  // looping it would be looping the song.
  const canLoop = (run?.segments.length ?? 0) <= 1;

  // Block chords get a groove for the bar — their block style's, else a backbeat. The groove is a constant out of DRUM_GROOVES, so its identity only changes
  // when the style does — which is exactly when playback should restart. Switching the drums off
  // only silences them, so it doesn't restart anything.
  const blockGroove = getBlockGroove(getBeatsPerBar(timeSignature), category);
  const drumGroove = getDrumGroove(rhythm ? getRhythmPattern(rhythm).drums : blockGroove);

  // Parts played through can each be set to their own style, so each pass is drummed with its own
  const drummedRun = useMemo(
    () =>
      run && {
        ...run,
        segments: run.segments.map((segment) => ({
          ...segment,
          drumGroove: getDrumGroove(segment.rhythm ? getRhythmPattern(segment.rhythm).drums : blockGroove),
        })),
      },
    [run, blockGroove],
  );

  // The same object while the levels are, since the player ramps every layer when it changes
  const { brass, guitar, acoustic, violin } = bandSettings;
  const bandVolumes = useMemo(
    () => ({ brass: brass.volume, guitar: guitar.volume, acoustic: acoustic.volume, violin: violin.volume }),
    [brass.volume, guitar.volume, acoustic.volume, violin.volume],
  );

  const {
    isLoading,
    isPlaying,
    error,
    play,
    stop,
    activeNotes,
    currentSegment,
    currentBar,
    currentChord,
    countInBeat,
    beat,
    getSpectrum,
    chordsCut,
  } = useChordGridPlayer(drummedRun, isActive, {
    timeSignature,
    metronome: mix.metronome,
    metronomeVolume: mix.clickLevel,
    instrumentVolume,
    loop: loop && canLoop,
    countIn: mix.countIn,
    startBar,
    drumGroove,
    drumVolume: drums ? drumVolume : 0,
    melodyVolume,
    bassVolume,
    padVolume,
    bandVolumes,
    effects: effects || NO_EFFECTS,
    chordsUnderMelody: CHORDS_UNDER_MELODY,
    callouts: calloutTexts,
    speakCallouts: calloutsOn,
    calloutVolume,
    calloutLang,
  });

  // Watches whatever download play sets off; nothing is fetched merely by opening a modal. The first
  // part's instruments: a later part's in a run are fetched as play builds them.
  const firstInstrumentId = parts[0]?.instrumentId;
  const firstMelodyInstrumentId = parts[0]?.melody?.instrumentId || '';
  const samples = useSampleLoader(firstInstrumentId, isActive);
  const melodySamples = useSampleLoader(firstMelodyInstrumentId, isActive && hasMelody);
  const bassSamples = useSampleLoader(BASS_INSTRUMENT_ID, isActive && bassPlays);
  const padSamples = useSampleLoader(padSampledId, isActive && Boolean(padSampledId));
  // Each extra player's recordings, only where the run plays it
  const bandSampledId = (id) => (run?.bandNotes[id].length > 0 ? band.find((layer) => layer.id === id).instrument : '');
  const bandLoaders = [
    ['brass', useSampleLoader(bandSampledId('brass'), isActive && Boolean(bandSampledId('brass')))],
    ['guitar', useSampleLoader(bandSampledId('guitar'), isActive && Boolean(bandSampledId('guitar')))],
    ['acoustic', useSampleLoader(bandSampledId('acoustic'), isActive && Boolean(bandSampledId('acoustic')))],
    ['violin', useSampleLoader(bandSampledId('violin'), isActive && Boolean(bandSampledId('violin')))],
  ].map(([id, samples]) => (bandSampledId(id) ? { samples, instrumentId: bandSampledId(id) } : IDLE_LOADER));
  // The electric's palm-muted chugs, on a voice of their own
  const mutedSampledId = run?.bandNotes.guitar.some(({ instrumentId }) => instrumentId === MUTED_GUITAR_ID)
    ? MUTED_GUITAR_ID
    : '';
  const mutedSamples = useSampleLoader(mutedSampledId, isActive && Boolean(mutedSampledId));

  // Closing shouldn't leave the chords playing on behind whatever opened them
  useEffect(() => {
    if (!isActive) stop();
  }, [isActive, stop]);

  const { playLabel, isFetchingSamples, playBusy, playProgress } = usePlayLabel(
    [
      { samples, instrumentId: firstInstrumentId },
      hasMelody && melodyOn ? { samples: melodySamples, instrumentId: firstMelodyInstrumentId } : IDLE_LOADER,
      bassPlays ? { samples: bassSamples, instrumentId: BASS_INSTRUMENT_ID } : IDLE_LOADER,
      padSampledId ? { samples: padSamples, instrumentId: padSampledId } : IDLE_LOADER,
      ...bandLoaders,
      mutedSampledId ? { samples: mutedSamples, instrumentId: mutedSampledId } : IDLE_LOADER,
    ],
    { isLoading, isPlaying },
  );

  // Reset in the mixer: every level back where it opens the first time, and every player back on.
  // The melody and the callouts are left as they are, being switched on for a reason. The players
  // that change the notes scheduled come back on only once stopped, as their own switches do.
  const isBusy = isPlaying || isLoading;
  const mixLevels = [
    [instrumentVolume, setInstrumentVolume, DEFAULT_INSTRUMENT_VOLUME],
    [mix.clickVolume, mix.setClickVolume, DEFAULT_CLICK_VOLUME],
    [drumVolume, setDrumVolume, DEFAULT_DRUM_VOLUME],
    [bassVolume, setBassVolume, DEFAULT_BASS_VOLUME],
    [padVolume, setPadVolume, DEFAULT_PAD_VOLUME],
    ...band.map((layer) => [layer.volume, layer.setVolume, DEFAULT_BAND_VOLUME]),
    [melodyVolume, setMelodyVolume, DEFAULT_MELODY_VOLUME],
    [calloutVolume, setCalloutVolume, DEFAULT_CALLOUT_VOLUME],
    [mix.metronome, mix.setMetronome, true],
    [drums, setDrums, true],
  ];
  const mixPlayers = [[bassOn, setBassOn, true], [padOn, setPadOn, true], ...band.map((layer) => [layer.on, layer.setOn, true])];
  const isChanged = ([value, , preset]) => (typeof preset === 'number' ? Math.abs(value - preset) > 1e-6 : value !== preset);
  const canResetMix = mixLevels.some(isChanged) || (!isBusy && mixPlayers.some(isChanged));
  const resetMix = () =>
    [...mixLevels, ...(isBusy ? [] : mixPlayers)].forEach((row) => {
      if (isChanged(row)) row[1](row[2]);
    });

  // How long the chord ringing right now rings for, in seconds — what the views time the rule
  // under it against. Held here rather than in the player because it is the chart's reading of the chord,
  // not the transport's: the same number whether the clip is looping, counting in or paused.
  const segment = run?.segments[currentSegment];
  const ringing = segment?.strikes[currentChord];
  const currentChordSeconds = ringing ? ringing.beats * segment.secondsPerBeat : 0;

  return {
    midiJson: run?.midiJson || null,
    segments: run?.segments || [],
    currentSegment: segment ? currentSegment : NO_SEGMENT,
    currentBar,
    currentChord,
    currentChordSeconds,
    countInBeat,
    beat,
    getSpectrum,
    activeNotes,
    isPlaying,
    isLoading,
    error,
    play,
    stop,
    playLabel,
    playBusy,
    playProgress,
    canPlay: Boolean(run) && !isLoading && !isFetchingSamples,
    samples,
    metronome: mix.metronome,
    setMetronome: mix.setMetronome,
    countIn: mix.countIn,
    setCountIn: mix.setCountIn,
    clickVolume: mix.clickVolume,
    setClickVolume: mix.setClickVolume,
    loop: loop && canLoop,
    setLoop,
    canLoop,
    instrumentVolume,
    setInstrumentVolume,
    chordsCut,
    drums,
    toggleDrums,
    drumVolume,
    setDrumVolume,
    voicing,
    toggleVoicing,
    hasMelody,
    melodyOn,
    toggleMelody,
    melodyVolume,
    setMelodyVolume,
    hasBass,
    bassOn,
    toggleBass,
    bassVolume,
    setBassVolume,
    hasPad,
    padOn,
    togglePad,
    padVolume,
    setPadVolume,
    band,
    hasCallouts: canSpeak && Boolean(calloutTexts),
    calloutsOn,
    toggleCallouts,
    calloutVolume,
    setCalloutVolume,
    canResetMix,
    resetMix,
    effects: effects || NO_EFFECTS,
    setEffect: onEffectsChange && ((id, bus, value) => onEffectsChange(setSend(effects, id, bus, value))),
    clearEffects: onEffectsChange && (() => onEffectsChange(null)),
  };
};

export default useChordBarsPreview;
